/**
 * KI-LEISTE UNTER DER KOPFLEISTE — nur fuer den Betreiber (Rafael, 03.10.2026 14:09).
 * Ersetzt dort den Sammel-Chip „KI 4/9 frei · höchste …": alle Zugaenge nebeneinander,
 * je Zugang eine gleich grosse Box: Name, Wochen- und Stundenlimit als Balken mit Restzeit. Eine Zeile am
 * Mac, im schmalen Fenster (iPad) bricht sie um. Jede Zelle gleich breit, Inhalte in festen
 * Spalten (Rafael, 03.10.2026 15:00: „symmetrisch … jedes Element gleich groß"). Ein Klick oeffnet das bisherige Menue
 * mit „KI-Zugänge verwalten".
 * PARTNER (Rafael, 03.10.2026 16:35, Option a: „Alle vier, neutral benannt"): dieselben Boxen
 * statt des Sammel-Chips, aber nur die vier Hauptzugaenge des Hauses („KI 1"–„KI 4",
 * nurHausZugaenge), ohne Menue — es gibt fuer sie nichts zu verwalten, und das Menue zeigte
 * Prozente. Prozent nur im Tooltip. Die Daten kuerzt der Server (server/lib/verbrauch-sicht.ts).
 * Rechenteil: lib/kiLeiste.ts. Gestaltung: styles/ki-leiste.css (nur --tn-* Variablen).
 */
import type { ReactNode } from 'react';
import KopfMenue from './KopfMenue';
import ApiReserve from './ApiReserve';
import { accountAccessLabel, accountAccessShortLabel } from '../types';
import type { AccountHealth, UsageAccount } from '../lib/kiSammelStand';
import { KI_LEISTE_VOLL_AB, kiLeistenEintraege, nurHausZugaenge, kiTooltipZeile, kiLeistenFarbe, type KiFenster, type KiLeistenEintrag } from '../lib/kiLeiste';
import '../styles/ki-leiste.css';

/**
 * BOX JE ZUGANG (Rafael, 03.10.2026 15:24: „als Box formatiert … oben wie lang das Wochenlimit
 * noch dauert, z. B. 1T, und unten das Stundenlimit, z. B. 2h … das % eigentlich weglassen … die
 * Nummer einfach über den Balken und die Balken von links nach rechts"). Oben der Name, darunter
 * zwei gleich lange Balken mit ihrer Restzeit. Prozent nur im Tooltip und im Menue. Ist ein Limit
 * voll, ist die Restzeit „wieder frei in" und die Box rot umrandet.
 */
function Zeile({ f, art, voll }: { f: KiFenster | null; art: 'woche' | 'stunde'; voll: boolean }) {
  // Kein solches Limit (Codex ohne Stundenfenster): Zeile bleibt leer, die Box gleich hoch.
  if (!f) return <><span className="ki-leiste-spur ki-leiste-spur-leer" /><span className="ki-leiste-rest" /></>;
  return <>
    <span className={`ki-leiste-spur ki-leiste-${art}`} aria-hidden="true">
      <span className="ki-leiste-fuellung" style={{ width: `${f.prozent === null ? 0 : Math.min(100, f.prozent)}%`, background: kiLeistenFarbe(f.prozent) }} />
    </span>
    <span className="ki-leiste-rest" data-voll={voll || undefined}>{f.prozent === null ? '?' : f.rest}</span>
  </>;
}

function Zugang({ e }: { e: KiLeistenEintrag }) {
  const name = accountAccessShortLabel(e.accountId);
  const woche: KiFenster = e.woche;
  const stunde: KiFenster | null = e.unbekannt ? { prozent: null, rest: '', genau: '' } : e.kurzzeit;
  const wocheVoll = (woche.prozent ?? 0) >= KI_LEISTE_VOLL_AB;
  const stundeVoll = (stunde?.prozent ?? 0) >= KI_LEISTE_VOLL_AB;
  const titel = [
    accountAccessLabel(e.accountId),
    kiTooltipZeile('Woche', woche),
    kiTooltipZeile('Stunden', stunde),
    e.gesperrt ? '⛔ Gesperrt oder Zugangskonfiguration widersprüchlich — Betrieb prüfen lassen.' : null,
  ].filter(Boolean).join('\n');
  const zustand = e.gesperrt || wocheVoll || stundeVoll ? 'rot' : e.unbekannt ? 'unbekannt' : 'ok';
  return (
    <span className="ki-leiste-zugang" data-zustand={zustand} data-ai-id="cui-ki-leiste-zugang" title={titel}>
      <span className="ki-leiste-name">{e.gesperrt && '⛔ '}{name}</span>
      <Zeile f={woche} art="woche" voll={wocheVoll} />
      <Zeile f={stunde} art="stunde" voll={stundeVoll} />
    </span>
  );
}

export default function KiLeiste({ accounts, health, partner = false, children, zusatz }: {
  accounts: UsageAccount[];
  health: Record<string, AccountHealth>;
  /** Partner: nur die vier Hauptzugaenge des Hauses, kein Menue. */
  partner?: boolean;
  /** Inhalt des bisherigen Menues „KI-Zugänge & Verbrauch" (Balken + Verwaltung) — nur Betreiber. */
  children?: ReactNode;
  zusatz?: ReactNode;
}) {
  const eintraege = kiLeistenEintraege(partner ? nurHausZugaenge(accounts) : accounts, health);
  const boxen = eintraege.length
    ? eintraege.map(e => <Zugang key={e.accountId} e={e} />)
    : <span className="ki-leiste-leer">KI-Zugänge: noch keine Kapazitätsdaten — unbekannt, nicht 0 %</span>;
  if (partner) {
    return (
      <div className="ki-leiste" data-ai-id="cui-ki-leiste" data-partner="">
        <div className="ki-leiste-knopf ki-leiste-nur-anzeige" role="group" aria-label="KI-Zugänge"
          title={'KI-Zugänge des Hauses — je Box oben das Wochenlimit, unten das Stundenlimit; daneben, wann es wieder frei wird. „?" = keine gültige Messung.\nProzente im Tooltip jeder Box.'}>
          {boxen}
        </div>
      </div>
    );
  }
  return (
    <div className="ki-leiste" data-ai-id="cui-ki-leiste" style={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
      <KopfMenue aiId="cui-ki-sammel" menueTitel="KI-Zugänge & Verbrauch" breite={720} klasse="ki-leiste-knopf"
        knopfTitel={'KI-Zugänge — je Box oben das Wochenlimit, unten das Stundenlimit; daneben, wann es wieder frei wird. „?" = keine gültige Messung.\nProzente im Tooltip jeder Box. Klick: Menü mit „KI-Zugänge verwalten".'}
        knopf={boxen}>
        {children}
      </KopfMenue>
      </div>
      <ApiReserve />
      {zusatz}
    </div>
  );
}
